// src/pages/ViewCard.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingScreen from '../components/LoadingScreen';
import { Link } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://192.168.0.146:8000/api';
const AUTH_KEY = 'authenticated_card'; // New key for successful auth

const ViewCard = () => {
  const { card_uid } = useParams();
  const navigate = useNavigate();
  const [videoUrl, setVideoUrl] = useState('');
  const [frontPicture, setFrontPicture] = useState(null); // For LoadingScreen background
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    // Security check: Ensure user is authenticated for this card_uid
    if (localStorage.getItem(`${AUTH_KEY}_${card_uid}`) !== 'true') {
      navigate(`/card_uid/${card_uid}`); // Redirect to CheckCard if not authenticated
      return;
    }

    const fetchCardData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/card_uid/${card_uid}/`);
        if (response.data.video) {
          setVideoUrl(`${API_BASE_URL.replace('/api', '')}${response.data.video}`);
        }
        if (response.data.front_picture) {
          setFrontPicture(`${API_BASE_URL.replace('/api', '')}${response.data.front_picture}`);
        }
      } catch (err) {
        setError('Грешка при зареждане на видео');
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 3000); // 3-second loading delay
      }
    };
    fetchCardData();
  }, [card_uid, navigate]);

  useEffect(() => {
    if (!loading && videoUrl && videoRef.current) {
      const videoElement = videoRef.current;

      const enterFullscreen = () => {
        if (videoElement.requestFullscreen) {
          videoElement.requestFullscreen();
        } else if (videoElement.webkitRequestFullscreen) { // Safari
          videoElement.webkitRequestFullscreen();
        } else if (videoElement.msRequestFullscreen) { //IE11
          videoElement.msRequestFullscreen();
        } else if (videoElement.webkitEnterFullScreen) { // iOS Safari
          videoElement.webkitEnterFullScreen();
        }
      };

      // Attempt to play the video
      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            enterFullscreen();
          })
          .catch((err) => {
            console.error('Error playing video:', err);
          });
      }
    }
  }, [loading, videoUrl]);

  const handleDownload = async () => {
    if (videoUrl) {
      try {
        const response = await fetch(videoUrl);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = videoUrl.split('/').pop() || 'video.mp4';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Download error:', err);
        setError('Грешка при изтегляне на видео');
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Сигурни ли сте, че искате да изтриете видеото?')) {
      try {
        await axios.delete(`${API_BASE_URL}/delete_video/${card_uid}/`);
        localStorage.removeItem(`${AUTH_KEY}_${card_uid}`); // Clear auth key on delete
        navigate(`/upload/${card_uid}`);
      } catch (err) {
        setError('Грешка при изтриване на видео');
      }
    }
  };

  if (loading) return <LoadingScreen backgroundImage={frontPicture} />;
  if (error) return <div className="text-center text-red-600 min-h-screen flex items-center justify-center">{error}</div>;

  return (
    <div 
      className="min-h-screen flex flex-col gap-0 md:items-center"
    >
      {videoUrl ? (
        <div className="flex flex-col min-h-screen gap-0 md:min-h-0 md:max-w-[1200px] md:w-[80vw] md:mx-auto md:p-2">
          {/* Video Preview */}
          <div className="relative flex-1 flex md:p-0">
            <div className="w-full h-100 md:w-full md:h-[80vh] md:max-h-[800px] bg-black md:rounded-lg md:shadow-lg overflow-hidden">
              <video
                ref={videoRef}
                controls
                autoPlay
                muted
                src={videoUrl}
                className="w-full h-full object-contain"
              >
                Вашият браузър не поддържа видео таг.
              </video>
            </div>
          </div>
          {/* Buttons Section (always visible below video) */}
          <div className="p-4 flex flex-col space-y-1 md:flex-row md:space-x-1 md:space-y-0 md:justify-center md:w-full ounded-lg shadow-md">
            <button onClick={handleDownload} className="w-full md:w-auto py-3 px-6 bg-gradient-to-r from-green-500 to-green-700 text-white font-semibold rounded-lg shadow-md hover:from-green-600 hover:to-green-800 flex items-center justify-center transition-all duration-300 transform hover:scale-105">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Изтегли видео
            </button>
            <button onClick={handleDelete} className="w-full md:w-auto py-3 px-6 bg-gradient-to-r from-red-500 to-red-700 text-white font-semibold rounded-lg shadow-md hover:from-red-600 hover:to-red-800 flex items-center justify-center transition-all duration-300 transform hover:scale-105">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Изтрий видео
            </button>
            <Link
              to="/terms"
              className="w-full md:w-auto py-3 px-6 bg-gradient-to-r from-gray-500 to-gray-700 text-white font-semibold rounded-lg shadow-md hover:from-gray-600 hover:to-gray-800 flex items-center justify-center transition-all duration-300 transform hover:scale-105"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              Общи условия
            </Link>
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-600 min-h-screen flex flex-col items-center justify-center">
          <p>Не е намерено видео. Моля, качете едно.</p>
          <Link
            to={`/upload/${card_uid}`}
            className="mt-4 py-3 px-6 bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-blue-800 transition-all duration-300 transform hover:scale-105"
          >
            Качи видео
          </Link>
        </div>
      )}
    </div>
  );
};

export default ViewCard;